// Permission reading on the screen side (plan 7.2 · 8.2.3). The server decides; the web only reads the EFFECTIVE
// permissions it sends with GET /api/v1/auth/me (permission key → 'self' | 'team' | 'all', absent = off) and never
// derives anything from a role name. Menu, bottom tabs and scope choices follow the mockup core.js rules exactly.
import type { Scope } from '@aiax-hub/contracts';
import type { IconName } from '../ui/icon-paths';
import type { ViewScope } from '../state/url';

export type ScopeValue = 'off' | Scope;
/** Effective permissions as the server sends them. */
export type EffectivePerms = Readonly<Record<string, ScopeValue | undefined>>;

export const RANK: Readonly<Record<ScopeValue, number>> = { off: 0, self: 1, team: 2, all: 3 };

/** Old (v0) permission names still accepted in code, mapped to v1 keys (mockup data.js PERM_ALIASES). */
export const PERM_ALIASES: Readonly<Record<string, string>> = { 'stats.export': 'export.aggregate', 'connections.manage': 'devices.revoke_any' };

export function scopeOf(perms: EffectivePerms | null | undefined, key: string): ScopeValue {
  const v = perms?.[PERM_ALIASES[key] ?? key] ?? 'off';
  // everyone manages their own connections (v0 'connections.manage' was always at least self)
  if (key === 'connections.manage' && v === 'off') return 'self';
  return v in RANK ? v : 'off';
}

/** Is the permission on (with `need`, at least that wide)? */
export function can(perms: EffectivePerms | null | undefined, key: string, need?: Scope): boolean {
  const sc = scopeOf(perms, key);
  if (!need) return sc !== 'off';
  return RANK[sc] >= RANK[need];
}
export const canAny = (perms: EffectivePerms | null | undefined, keys: string | readonly string[]): boolean =>
  ([] as string[]).concat(keys as string[]).some((k) => can(perms, k));

/** View scope choices for a permission range: all → me / all (team filter inside all), team → me / team, self → me. */
export const scopesOf = (sc: ScopeValue): ViewScope[] => (sc === 'all' ? ['me', 'all'] : sc === 'team' ? ['me', 'team'] : ['me']);
/** The permission key that decides a screen's scope choices (route `scopePerm`, default usage.view). */
export const DEFAULT_SCOPE_PERM = 'usage.view';
export const allowedScopes = (perms: EffectivePerms | null | undefined, scopePerm = DEFAULT_SCOPE_PERM): ViewScope[] => scopesOf(scopeOf(perms, scopePerm));
export function defaultScope(perms: EffectivePerms | null | undefined, scopePerm = DEFAULT_SCOPE_PERM): ViewScope {
  const sc = scopeOf(perms, scopePerm);
  return sc === 'all' ? 'all' : sc === 'team' ? 'team' : 'me';
}
/** What the person asked for when this screen allows it, otherwise the screen's default. */
export function effectiveScope(perms: EffectivePerms | null | undefined, scopePerm: string | undefined, wanted: ViewScope | null): ViewScope {
  const allowed = allowedScopes(perms, scopePerm);
  return wanted && allowed.includes(wanted) ? wanted : defaultScope(perms, scopePerm);
}
/** The team filter exists only inside "all" on a screen whose scope permission is "all". */
export const teamFilterOn = (perms: EffectivePerms | null | undefined, scopePerm: string | undefined, scope: ViewScope): boolean =>
  scope === 'all' && scopeOf(perms, scopePerm ?? DEFAULT_SCOPE_PERM) === 'all';

export interface TeamRef {
  id: string;
  name: string;
  /** Fewer than 3 people (small-group protection, plan 7.6). */
  small?: boolean;
}
/** A small team can be picked only with real-name permission over everyone (otherwise one or two people's values show). */
export const teamPickable = (perms: EffectivePerms | null | undefined, team: TeamRef | undefined): boolean =>
  !!team && (!team.small || scopeOf(perms, 'people.identity') === 'all');

// ───────────────────────── menu (plan 8.2.2 order, 8.2.3 rules) ─────────────────────────
export type MenuId =
  | 'home'
  | 'logs'
  | 'stats'
  | 'people'
  | 'connect'
  | 'glossary'
  | 'me'
  | 'admin/roles'
  | 'admin/policy'
  | 'admin/audit'
  | 'admin/collection'
  | 'admin/cost';

export interface MenuItem {
  id: MenuId;
  /** Route path without the leading slash. */
  path: string;
  /** i18n key of the menu name; `${key}.desc` is the question the screen answers. */
  key: string;
  icon: IconName;
  group?: 'admin';
  show(perms: EffectivePerms | null | undefined): boolean;
}

const always = () => true;
export const MENU: readonly MenuItem[] = [
  { id: 'home', path: 'home', key: 'nav.home', icon: 'home', show: always },
  // everyone also holds the member role, so the activity list is there for all (stats viewers see only their own)
  { id: 'logs', path: 'logs', key: 'nav.logs', icon: 'list', show: (p) => can(p, 'logs.list') },
  { id: 'stats', path: 'stats', key: 'nav.stats', icon: 'chart', show: always },
  // people tab (people.view) or the team tab (stats.view at team scope or wider)
  { id: 'people', path: 'people', key: 'nav.people', icon: 'users', show: (p) => can(p, 'people.view') || can(p, 'stats.view', 'team') },
  { id: 'connect', path: 'connect', key: 'nav.connect', icon: 'plug', show: always },
  { id: 'glossary', path: 'glossary', key: 'nav.glossary', icon: 'book', show: always },
  { id: 'me', path: 'me', key: 'nav.settings', icon: 'user', show: always },
  // Investigators use the approval workspace here without gaining role-management access.
  { id: 'admin/roles', path: 'admin/roles', key: 'nav.admin.roles', icon: 'key', group: 'admin', show: (p) => can(p, 'roles.manage') || can(p, 'access.approve', 'all') || can(p, 'logs.prompt', 'all') || can(p, 'logs.answer', 'all') },
  // operations (deletion.process) see only the deletion tab of the recording policy; any key of a policy tab opens the
  // screen (contracts SCREENS SCR-12 · POLICY_TAB_RULE: notice / deletion / legal hold / retention job holders too)
  {
    id: 'admin/policy',
    path: 'admin/policy',
    key: 'nav.admin.policy',
    icon: 'shield',
    group: 'admin',
    show: (p) => canAny(p, ['policy.manage', 'notice.manage', 'deletion.process', 'legal_hold.manage', 'retention.manage']),
  },
  { id: 'admin/audit', path: 'admin/audit', key: 'nav.admin.audit', icon: 'scroll', group: 'admin', show: (p) => can(p, 'audit.view') },
  { id: 'admin/collection', path: 'admin/collection', key: 'nav.admin.system', icon: 'pulse', group: 'admin', show: (p) => can(p, 'system.view') },
  { id: 'admin/cost', path: 'admin/cost', key: 'nav.admin.cost', icon: 'coins', group: 'admin', show: (p) => can(p, 'policy.manage') || can(p, 'budget.manage') },
];

export const menuFor = (perms: EffectivePerms | null | undefined): MenuItem[] => MENU.filter((m) => m.show(perms));
export const menuItem = (id: string): MenuItem | undefined => MENU.find((m) => m.id === id);

/** Bottom tabs on phones (plan 8.2.4): with people → home·logs·people·stats, otherwise home·logs·stats·glossary (+ "more"). */
export function bottomTabsFor(perms: EffectivePerms | null | undefined): MenuId[] {
  const ids = menuFor(perms).map((m) => m.id);
  const want: MenuId[] = ids.includes('people') ? ['home', 'logs', 'people', 'stats'] : ['home', 'logs', 'stats', 'glossary'];
  const out = want.filter((id) => ids.includes(id));
  (['glossary', 'connect', 'me'] as MenuId[]).forEach((id) => {
    if (out.length < 4 && !out.includes(id) && ids.includes(id)) out.push(id);
  });
  return out;
}

/** The menu entry a path belongs to (longest matching path). */
export function menuItemForPath(pathname: string): MenuItem | null {
  const path = pathname.replace(/^\/+|\/+$/g, '') || 'home';
  let best: MenuItem | null = null;
  for (const m of MENU) if ((path === m.path || path.startsWith(m.path + '/')) && (!best || m.path.length > best.path.length)) best = m;
  return best;
}
